import React, { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { PasswordInput } from "../components/ui/password-input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { BackBar } from "../components/BackBar";
import { AlertTriangle, KeyRound } from "lucide-react";

export default function ResetPasswordPage() {
  const { lang } = useLang();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) {
      toast.error(lang === "fr" ? "Les deux mots de passe ne correspondent pas." : "Passwords don't match.");
      return;
    }
    if (pw.length < 10) {
      toast.error(lang === "fr" ? "Au moins 10 caractères requis." : "At least 10 characters required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success(lang === "fr" ? "Mot de passe mis à jour. Connectez-vous." : "Password updated. Please sign in.");
      nav("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-6 py-12" data-testid="reset-page-invalid">
        <BackBar to="/login" fallbackLabelFr="Connexion" fallbackLabelEn="Login" />
        <Card className="border-destructive/30 bg-destructive/[0.02]">
          <CardContent className="p-6 space-y-3 text-center">
            <AlertTriangle className="w-10 h-10 text-destructive mx-auto" />
            <h2 className="font-display font-semibold">
              {lang === "fr" ? "Lien invalide" : "Invalid link"}
            </h2>
            <p className="text-sm text-muted-foreground">
              {lang === "fr"
                ? "Le lien de réinitialisation est incomplet ou a expiré. Demandez un nouveau lien."
                : "The reset link is incomplete or has expired. Please request a new one."}
            </p>
            <Link to="/forgot-password" className="inline-block text-sm text-accent font-medium" data-testid="goto-forgot">
              {lang === "fr" ? "Demander un nouveau lien" : "Request a new link"}
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-6 py-12" data-testid="reset-page">
      <BackBar to="/login" fallbackLabelFr="Connexion" fallbackLabelEn="Login" />
      <p className="overline mb-3">OFA France</p>
      <h1 className="font-serif text-4xl tracking-tighter mb-2">
        {lang === "fr" ? "Nouveau mot de passe" : "New password"}
      </h1>
      <p className="text-muted-foreground mb-8 text-sm">
        {lang === "fr"
          ? "Choisissez un mot de passe d'au moins 10 caractères, avec une majuscule, une minuscule, un chiffre et un caractère spécial."
          : "Choose a password of at least 10 characters, with one uppercase, one lowercase, one digit and one special character."}
      </p>

      <form onSubmit={onSubmit} className="space-y-5">
        <div>
          <Label className="text-sm">{lang === "fr" ? "Nouveau mot de passe" : "New password"}</Label>
          <PasswordInput
            data-testid="reset-pw"
            required
            minLength={10}
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            autoFocus
          />
        </div>
        <div>
          <Label className="text-sm">{lang === "fr" ? "Confirmer le mot de passe" : "Confirm password"}</Label>
          <PasswordInput
            data-testid="reset-pw2"
            required
            minLength={10}
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
          />
        </div>
        <Button
          data-testid="reset-submit"
          type="submit"
          disabled={submitting || !pw || !pw2}
          className="w-full bg-primary text-primary-foreground"
        >
          <KeyRound className="w-4 h-4 mr-1.5" />
          {submitting
            ? (lang === "fr" ? "Mise à jour…" : "Updating…")
            : (lang === "fr" ? "Réinitialiser le mot de passe" : "Reset password")}
        </Button>
      </form>
    </div>
  );
}
